import { useId, useState } from "react";
import type { RepresentationAnswer } from "@ondo/shared";

import { CounterpartyCheck } from "../counterparty/CounterpartyCheck.tsx";
import { WorkThumb } from "../lib/WorkThumb.tsx";
import { INBOX_COPY } from "../representations/representations.constants.ts";
import { VERIFICATION_COPY } from "./verifications.constants.ts";
import type { RepresentationCard } from "./verifications.model.ts";

type Props = {
  requests: RepresentationCard[];
  /** Signed thumbnails by storage path, so a card can show the work. */
  images: Map<string, string>;
  busy: boolean;
  onAnswer: (
    requestId: string,
    body: { answer: RepresentationAnswer; note?: string | null },
  ) => void;
};

/**
 * The third tab: agents asking to represent the reader's works.
 *
 * IT SHOWS THE WORK, which is what makes it a different card from
 * `AffiliationClaims` rather than that one with different words. A claim asks
 * "is this true" and its subject is a person; this asks "may they act for you"
 * and its subject is an object — and nobody can answer that without seeing
 * which object. Hence the photograph, the title and the artist at the head.
 *
 * IT SAYS WHAT GRANTING WOULD DO, in the same sentence the notification detail
 * uses. A mandate is narrower than it sounds — the record, the catalogue entry
 * and the history, and nothing that disposes of the work or widens its
 * audience — and a reader who imagined they were handing over the ability to
 * sell would decline something harmless, or grant something they misread.
 */
export function RepresentationRequests({ requests, images, busy, onAnswer }: Props) {
  const [open, setOpen] = useState<{
    requestId: string;
    answer: RepresentationAnswer;
  } | null>(null);
  const [text, setText] = useState("");
  const fieldId = useId();

  return (
    <ul className="verifications__list">
      {requests.map((request) => (
        <li
          key={request.requestId}
          id={`representation-${request.requestId}`}
          className={`verifications__card ${
            request.awaiting ? "verifications__card--awaiting" : ""
          }`}
        >
          <header className="verifications__card-head">
            <WorkThumb
              imageUrl={request.imagePath ? (images.get(request.imagePath) ?? null) : null}
              title={request.title}
            />
            <div className="verifications__card-title">
              <h3>{request.title}</h3>
              <p>
                {request.attributedName}
                {request.year ? ` · ${request.year}` : ""}
              </p>
            </div>
            <span
              className={`verifications__stamp verifications__stamp--${
                request.awaiting ? "awaiting" : "resolved"
              }`}
            >
              {request.awaiting ? VERIFICATION_COPY.awaitingBadge : request.outcome}
            </span>
          </header>

          <section className="verifications__asked">
            <h4>{INBOX_COPY.asked}</h4>
            {/* The agent by name and by ONDO id. Not a disclosure this registry
                rations — they put their own name on the request, and there
                would be nobody to answer if it were withheld. */}
            <p>
              <strong>
                {request.agentName} · {request.agentOndoId}
              </strong>
              {request.agentVerified ? " · Verified" : ""}
              {/* OND-81: the asker put their name to this; the owner may check
                  who they are before answering. */}
              <CounterpartyCheck ondoId={request.agentOndoId} />
            </p>
            <p>{INBOX_COPY.askedDetail(request.agentName)}</p>
            {request.note && <p>“{request.note}”</p>}
            {request.responseNote && <p>{request.responseNote}</p>}
          </section>

          {/* Offered only when the database said so. `canAnswer` comes off the
              same set the answer admits — see `toRepresentationCard`. */}
          {request.canAnswer && open?.requestId !== request.requestId && (
            <div className="verifications__buttons">
              <button
                type="button"
                className="verifications__button verifications__button--confirm"
                onClick={() => {
                  setText("");
                  setOpen({ requestId: request.requestId, answer: "grant" });
                }}
              >
                {INBOX_COPY.grant}
              </button>
              <button
                type="button"
                className="verifications__button verifications__button--decline"
                onClick={() => {
                  setText("");
                  setOpen({ requestId: request.requestId, answer: "decline" });
                }}
              >
                {INBOX_COPY.decline}
              </button>
            </div>
          )}

          {open?.requestId === request.requestId && (
            <div className="verifications__composer">
              <label htmlFor={fieldId}>{INBOX_COPY.noteLabel}</label>
              <textarea
                id={fieldId}
                rows={3}
                value={text}
                placeholder={INBOX_COPY.notePlaceholder}
                onChange={(event) => setText(event.target.value)}
              />
              {/* Says the note is optional on BOTH answers. A required reason
                  would turn a decline into a judgement of somebody's
                  professional conduct with no right of reply. */}
              <p className="verifications__help">{INBOX_COPY.noteHelp}</p>
              <div className="verifications__buttons">
                <button
                  type="button"
                  className="verifications__button"
                  onClick={() => setOpen(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="verifications__button verifications__button--send"
                  disabled={busy}
                  onClick={() => {
                    onAnswer(request.requestId, {
                      answer: open.answer,
                      note: text.trim() || null,
                    });
                    setOpen(null);
                  }}
                >
                  {open.answer === "grant" ? INBOX_COPY.grant : INBOX_COPY.decline}
                </button>
              </div>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}
